import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  ArrowPathIcon,
  HomeModernIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import CustomToast from "../common/CustomToast";
import LoadingSpinner from "../common/LoadingSpinner";
import FamilyReasonModal from "./FamilyReasonModal";
import {
  useCancelFamilyInvitation,
  useCustomerFamily,
  useDissolveFamily,
  useEraseFamilyMember,
  useRemoveFamilyGoalImage,
  useRemoveFamilyMember,
  useResetFamilyMemberCredential,
} from "../../hooks/useCustomerFamily";
import type {
  CustomerFamilyAccountView,
  FamilyBadge,
  FamilyEvent,
  FamilyGoalAdminView,
  FamilyGoalHistoryEntry,
  FamilyMemberAdminRow,
} from "../../services/api";
import {
  familyAdminErrorCode,
  familyAdminErrorMessage,
} from "../../utils/familyAdminErrors";

export const FAMILY_ACCOUNT_SECTION_ID = "family-account-section";

export type FamilyMemberAction = "remove" | "reset" | "cancel" | "erase";

const TERMINAL_STATUSES = ["expired", "cancelled", "removed", "left"];

/** Support actions offered for a member row (none for erased rows). */
export function familyMemberActions(
  member: Pick<FamilyMemberAdminRow, "status" | "erased">,
): FamilyMemberAction[] {
  if (member.erased) return [];
  if (member.status === "active") return ["remove", "reset"];
  if (member.status === "pending") return ["cancel"];
  if (TERMINAL_STATUSES.includes(member.status)) return ["erase"];
  return [];
}

type PendingAction =
  | { kind: FamilyMemberAction; member: FamilyMemberAdminRow }
  | { kind: "dissolve" }
  | { kind: "removeImage" };

const showToast = (message: string, type: "success" | "error" | "warning") =>
  toast.custom(<CustomToast message={message} type={type} />, {
    duration: type === "success" ? 4000 : 6000,
  });

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const formatEp = (value: number | null | undefined): string =>
  typeof value === "number" ? `${value.toLocaleString()} EP` : "—";

const memberName = (member: FamilyMemberAdminRow): string =>
  [member.firstName, member.lastName].filter(Boolean).join(" ") || "—";

/**
 * Who a confirm dialog is about: the name, else the (masked) e-mail, and
 * the member id. Ended rows often have no name any more.
 */
const memberDialogLabel = (member: FamilyMemberAdminRow): string => {
  const name = [member.firstName, member.lastName].filter(Boolean).join(" ");
  const label = name || member.email || member.emailMasked;
  return label ? `${label} (${member.memberId})` : member.memberId;
};

const humanize = (value: string): string => {
  const text = value.replace(/_/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
};

const STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
  pending:
    "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
  removed: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
  expired: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300",
  cancelled: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300",
  left: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300",
};

export const familyStatusBadgeClass = (status: string): string =>
  STATUS_BADGE[status] ?? STATUS_BADGE.expired;

const REMOVED_BY_LABELS: Record<string, string> = {
  admin: "Removed by the family admin",
  self: "Left the family",
  system: "Ended by the system",
  support: "Removed by support",
  owner_deleted: "Owner account deleted",
};

const CANCEL_REASON_LABELS: Record<string, string> = {
  admin_cancel: "Cancelled by the family admin",
  declined: "Declined",
  not_me: "Declined (not me)",
  email_registered: "E-mail already has an ePOINT account",
  joined_other_family: "Joined another family",
  dissolved: "Family dissolved",
  support: "Cancelled by support",
};

/** Backend enum `dissolvedReason`; the typed support reason lives in events. */
const DISSOLVED_REASON_LABELS: Record<string, string> = {
  support: "Dissolved by support",
  owner_deleted: "Owner account deleted",
};

const labelOrRaw = (labels: Record<string, string>, value: string) =>
  labels[value] ?? humanize(value);

/**
 * Only the backend's FAMILY_NOT_FOUND code means "no family". A 404 without
 * it (route not deployed, wrong base URL) is a real error and gets Retry.
 */
const isNoFamilyError = (error: unknown): boolean =>
  familyAdminErrorCode(error) === "FAMILY_NOT_FOUND";

const labelClass =
  "text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide";
const valueClass = "mt-1 text-sm text-gray-900 dark:text-white";
const thClass =
  "px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap";
const tdClass =
  "px-3 py-2 align-top text-sm text-gray-900 dark:text-white whitespace-nowrap";
const actionButtonClass =
  "text-xs font-medium px-2 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed";
const redActionClass = `${actionButtonClass} text-red-700 dark:text-red-300 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/30`;
const neutralActionClass = `${actionButtonClass} text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700`;

interface ActionCopy {
  title: string;
  description: string;
  warning: string;
  confirmLabel: string;
  pendingLabel: string;
}

const actionCopy = (
  action: PendingAction,
  view: CustomerFamilyAccountView,
): ActionCopy => {
  const who =
    "member" in action
      ? memberDialogLabel(action.member)
      : "";
  switch (action.kind) {
    case "remove":
      return {
        title: "Remove family member",
        description: who,
        warning:
          "The member is signed out on every device immediately and loses access to the family.",
        confirmLabel: "Remove member",
        pendingLabel: "Removing...",
      };
    case "reset":
      return {
        title: "Reset member credential",
        description: who,
        warning:
          "All sessions of this member end now. The member receives an e-mail to set up access again.",
        confirmLabel: "Reset credential",
        pendingLabel: "Resetting...",
      };
    case "cancel":
      return {
        title: "Cancel invitation",
        description: who,
        warning: "The invitation link stops working and the seat is freed.",
        confirmLabel: "Cancel invitation",
        pendingLabel: "Cancelling...",
      };
    case "erase":
      return {
        title: "Erase member data (GDPR)",
        description: who,
        warning:
          "Name and e-mail of this ended membership are erased permanently. The row stays in the audit log without personal data. This cannot be undone.",
        confirmLabel: "Erase data",
        pendingLabel: "Erasing...",
      };
    case "dissolve":
      return {
        title: "Dissolve family account",
        description: view.family?.familyId ?? "",
        warning:
          "All active members are removed and signed out, pending invitations are cancelled and the goal is deleted. The owner's customer account stays. This cannot be undone.",
        confirmLabel: "Dissolve family",
        pendingLabel: "Dissolving...",
      };
    case "removeImage":
    default:
      return {
        title: "Remove goal image",
        description: view.goal?.title ?? "",
        warning:
          "The image is deleted for the whole family. The goal itself stays.",
        confirmLabel: "Remove image",
        pendingLabel: "Removing...",
      };
  }
};

interface FamilyAccountSectionProps {
  // Owner customer id (CC…) of the customer shown in the detail modal.
  customerId: string;
  // Set when the customer was found through a family member's e-mail.
  familyBadge?: FamilyBadge;
  // Scroll the section into view once it mounts (search badge click).
  scrollIntoViewOnMount?: boolean;
}

const FamilyAccountSection: React.FC<FamilyAccountSectionProps> = ({
  customerId,
  familyBadge,
  scrollIntoViewOnMount = false,
}) => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const { data, error, isLoading, isError, isFetching, refetch } =
    useCustomerFamily(customerId, true);

  const removeMember = useRemoveFamilyMember();
  const cancelInvitation = useCancelFamilyInvitation();
  const dissolveFamily = useDissolveFamily();
  const resetCredential = useResetFamilyMemberCredential();
  const removeGoalImage = useRemoveFamilyGoalImage();
  const eraseMember = useEraseFamilyMember();

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );

  useEffect(() => {
    if (!scrollIntoViewOnMount) return;
    const element = sectionRef.current;
    if (element && typeof element.scrollIntoView === "function") {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [scrollIntoViewOnMount]);

  const mutationFor = (action: PendingAction | null) => {
    switch (action?.kind) {
      case "remove":
        return removeMember;
      case "reset":
        return resetCredential;
      case "cancel":
        return cancelInvitation;
      case "erase":
        return eraseMember;
      case "dissolve":
        return dissolveFamily;
      case "removeImage":
        return removeGoalImage;
      default:
        return null;
    }
  };

  const activeMutation = mutationFor(pendingAction);

  const openAction = (action: PendingAction) => {
    mutationFor(action)?.reset();
    setPendingAction(action);
  };

  const closeAction = useCallback(() => setPendingAction(null), []);

  const handleConfirm = (reason: string) => {
    const familyId = data?.family?.familyId;
    if (!pendingAction || !familyId) return;
    const done = () => setPendingAction(null);

    switch (pendingAction.kind) {
      case "remove":
        removeMember.mutate(
          { familyId, memberId: pendingAction.member.memberId, reason },
          {
            onSuccess: () => {
              done();
              showToast("Family member removed", "success");
            },
          },
        );
        return;
      case "reset":
        resetCredential.mutate(
          { familyId, memberId: pendingAction.member.memberId, reason },
          {
            onSuccess: (result) => {
              done();
              if (result && result.mailed === false) {
                showToast(
                  "Sessions ended, but the notice e-mail could not be sent. Contact the member another way.",
                  "warning",
                );
              } else {
                showToast("Member credential reset", "success");
              }
            },
          },
        );
        return;
      case "cancel":
        cancelInvitation.mutate(
          { familyId, memberId: pendingAction.member.memberId, reason },
          {
            onSuccess: () => {
              done();
              showToast("Invitation cancelled", "success");
            },
          },
        );
        return;
      case "erase":
        eraseMember.mutate(
          { familyId, memberId: pendingAction.member.memberId, reason },
          {
            onSuccess: () => {
              done();
              showToast("Member data erased", "success");
            },
          },
        );
        return;
      case "dissolve":
        dissolveFamily.mutate(
          { familyId, reason },
          {
            onSuccess: () => {
              done();
              showToast("Family account dissolved", "success");
            },
          },
        );
        return;
      case "removeImage":
        removeGoalImage.mutate(
          { familyId, reason },
          {
            onSuccess: (result) => {
              done();
              showToast(
                result && result.removed === false
                  ? "The goal had no image"
                  : "Goal image removed",
                result && result.removed === false ? "warning" : "success",
              );
            },
          },
        );
        return;
      default:
        return;
    }
  };

  const anyActionPending = [
    removeMember,
    cancelInvitation,
    dissolveFamily,
    resetCredential,
    removeGoalImage,
    eraseMember,
  ].some((mutation) => mutation.isPending);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div
          className="flex items-center space-x-3 py-4 text-sm text-gray-500 dark:text-gray-400"
          data-testid="family-account-loading"
        >
          <LoadingSpinner size="sm" className="!justify-start" />
          <span>Loading family account...</span>
        </div>
      );
    }

    if (isError && !data) {
      if (isNoFamilyError(error)) {
        return (
          <p
            className="text-sm text-gray-600 dark:text-gray-400"
            data-testid="family-account-empty"
          >
            This customer has no family account.
          </p>
        );
      }
      return (
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-4 py-3"
          data-testid="family-account-error"
        >
          <p className="text-sm text-red-700 dark:text-red-300" role="alert">
            {familyAdminErrorMessage(error)}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-200 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/40 disabled:opacity-50"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1.5" />
            {isFetching ? "Retrying..." : "Retry"}
          </button>
        </div>
      );
    }

    if (!data || !data.family) {
      return (
        <p
          className="text-sm text-gray-600 dark:text-gray-400"
          data-testid="family-account-empty"
        >
          This customer has no family account.
        </p>
      );
    }

    return (
      <FamilyAccountDetails
        view={data}
        onAction={openAction}
        onRefetch={() => refetch()}
        actionsDisabled={anyActionPending}
      />
    );
  };

  return (
    <div
      id={FAMILY_ACCOUNT_SECTION_ID}
      ref={sectionRef}
      className="border-b border-gray-200 dark:border-gray-700 pb-4 scroll-mt-4"
      data-testid="family-account-section"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <HomeModernIcon className="h-5 w-5 mr-2" />
          Family Account
        </h4>
        {data?.family && !data.family.dissolvedAt && (
          <button
            type="button"
            onClick={() => openAction({ kind: "dissolve" })}
            disabled={anyActionPending}
            className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Dissolve family
          </button>
        )}
      </div>

      {familyBadge && (
        <p
          className="mb-4 text-sm text-purple-800 dark:text-purple-200 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-md px-3 py-2"
          data-testid="family-account-badge-note"
        >
          Found through the e-mail of a family member ({familyBadge.status}).
          This customer is the family admin of that member.
        </p>
      )}

      {renderBody()}

      {data && pendingAction && (
        <FamilyReasonModal
          isOpen
          {...actionCopy(pendingAction, data)}
          isPending={Boolean(activeMutation?.isPending)}
          error={activeMutation?.error ?? undefined}
          onConfirm={handleConfirm}
          onClose={closeAction}
        />
      )}
    </div>
  );
};

interface FamilyAccountDetailsProps {
  view: CustomerFamilyAccountView;
  onAction: (action: PendingAction) => void;
  onRefetch: () => void;
  actionsDisabled: boolean;
}

const FamilyAccountDetails: React.FC<FamilyAccountDetailsProps> = ({
  view,
  onAction,
  onRefetch,
  actionsDisabled,
}) => {
  const family = view.family!;
  const dissolved = Boolean(family.dissolvedAt);

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Role</label>
          <p className={valueClass}>Family admin (owner)</p>
        </div>
        <div>
          <label className={labelClass}>Status</label>
          <p className="mt-1 flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                dissolved
                  ? "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                  : "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
              }`}
              data-testid="family-account-status"
            >
              {dissolved ? "Dissolved" : "Active"}
            </span>
            {!family.enabled && !dissolved && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                Family features off for this owner
              </span>
            )}
          </p>
        </div>
        <div>
          <label className={labelClass}>Seats used</label>
          <p className={valueClass} data-testid="family-account-seats">
            {family.seatsUsed}/{family.maxSeats}{" "}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              (including the admin and pending invitations)
            </span>
          </p>
        </div>
        <div>
          <label className={labelClass}>Family ID</label>
          <p className={`${valueClass} font-mono`}>{family.familyId}</p>
        </div>
        <div>
          <label className={labelClass}>Created At</label>
          <p className={valueClass}>{formatDateTime(family.createdAt)}</p>
        </div>
        {dissolved && (
          <div>
            <label className={labelClass}>Dissolved At</label>
            <p className={valueClass}>{formatDateTime(family.dissolvedAt)}</p>
            {family.dissolvedReason && (
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 whitespace-normal break-words">
                Reason:{" "}
                {labelOrRaw(DISSOLVED_REASON_LABELS, family.dissolvedReason)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Members */}
      <div>
        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          Members and invitations
        </h5>
        {view.members.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No members or invitations.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className={thClass}>Member / E-mail</th>
                  <th className={thClass}>Status</th>
                  <th className={thClass}>Dates</th>
                  <th className={thClass}>Ended by</th>
                  <th className={thClass}>Last login</th>
                  <th className={thClass}>Password</th>
                  <th className={thClass}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {view.members.map((member) => (
                  <FamilyMemberRow
                    key={member.memberId}
                    member={member}
                    onAction={onAction}
                    actionsDisabled={actionsDisabled}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Goal */}
      <FamilyGoalSummary
        goal={view.goal}
        onRemoveImage={() => onAction({ kind: "removeImage" })}
        onRefetch={onRefetch}
        actionsDisabled={actionsDisabled}
      />

      {/* Goal history */}
      <FamilyGoalHistory entries={view.goalHistory} />

      {/* Events */}
      <FamilyEvents events={view.events} />
    </div>
  );
};

interface FamilyMemberRowProps {
  member: FamilyMemberAdminRow;
  onAction: (action: PendingAction) => void;
  actionsDisabled: boolean;
}

const ACTION_LABELS: Record<FamilyMemberAction, string> = {
  remove: "Remove",
  reset: "Reset credential",
  cancel: "Cancel invitation",
  erase: "Erase (GDPR)",
};

const FamilyMemberRow: React.FC<FamilyMemberRowProps> = ({
  member,
  onAction,
  actionsDisabled,
}) => {
  const statusBadge = (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${familyStatusBadgeClass(
        member.status,
      )}`}
    >
      {humanize(member.status)}
    </span>
  );

  if (member.erased) {
    return (
      <tr data-testid={`family-member-row-${member.memberId}`}>
        <td className={tdClass}>
          <div className="text-gray-500 dark:text-gray-400">Erased member</div>
          <div className="text-xs font-mono text-gray-500 dark:text-gray-400">
            {member.memberId}
          </div>
        </td>
        <td className={tdClass}>{statusBadge}</td>
        <td
          className={`${tdClass} text-gray-600 dark:text-gray-400`}
          colSpan={5}
          data-testid="family-member-erased"
        >
          Erased {formatDateTime(member.erasedAt)} by {member.erasedBy || "—"}
        </td>
      </tr>
    );
  }

  const actions = familyMemberActions(member);
  const endedBy = [
    member.cancelReason
      ? labelOrRaw(CANCEL_REASON_LABELS, member.cancelReason)
      : null,
    member.removedBy ? labelOrRaw(REMOVED_BY_LABELS, member.removedBy) : null,
  ].filter(Boolean);

  return (
    <tr data-testid={`family-member-row-${member.memberId}`}>
      <td className={tdClass}>
        <div className="font-medium">{memberName(member)}</div>
        <div className="text-xs text-gray-600 dark:text-gray-300">
          {member.email || member.emailMasked || "—"}
        </div>
        <div className="text-xs font-mono text-gray-500 dark:text-gray-400">
          {member.memberId}
        </div>
      </td>
      <td className={tdClass}>{statusBadge}</td>
      <td className={`${tdClass} text-xs`}>
        <div>Invited: {formatDateTime(member.invitedAt)}</div>
        {member.status === "pending" && (
          <div>Expires: {formatDateTime(member.expiresAt)}</div>
        )}
        {member.acceptedAt && (
          <div>Accepted: {formatDateTime(member.acceptedAt)}</div>
        )}
        {(member.endedAt || member.removedAt) && (
          <div>Ended: {formatDateTime(member.endedAt || member.removedAt)}</div>
        )}
      </td>
      <td className={`${tdClass} text-xs`}>
        {endedBy.length ? endedBy.join(" · ") : "—"}
      </td>
      <td className={`${tdClass} text-xs`}>
        <div>{formatDateTime(member.lastLoginAt)}</div>
        {member.lastLoginCountry && <div>{member.lastLoginCountry}</div>}
      </td>
      <td className={`${tdClass} text-xs`}>
        {member.hasPassword ? "Set" : "Not set"}
      </td>
      <td className={tdClass}>
        {actions.length === 0 ? (
          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
        ) : (
          <div className="flex flex-col items-start gap-1.5">
            {actions.map((kind) => (
              <button
                key={kind}
                type="button"
                disabled={actionsDisabled}
                onClick={() => onAction({ kind, member })}
                className={
                  kind === "reset" ? neutralActionClass : redActionClass
                }
              >
                {ACTION_LABELS[kind]}
              </button>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
};

interface FamilyGoalSummaryProps {
  goal: FamilyGoalAdminView | null;
  onRemoveImage: () => void;
  onRefetch: () => void;
  actionsDisabled: boolean;
}

// Timers longer than 2^31-1 ms fire immediately in browsers.
const MAX_TIMEOUT_MS = 2147483647;

const FamilyGoalSummary: React.FC<FamilyGoalSummaryProps> = ({
  goal,
  onRemoveImage,
  onRefetch,
  actionsDisabled,
}) => {
  const imageUrl = goal?.imageUrl ?? null;
  const expiresAt = goal?.imageUrlExpiresAt ?? null;
  // The signed URL that failed to load (or had expired) and was already
  // refetched once: a second failure of the same URL shows a placeholder
  // instead of refetching in a loop.
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const refetchedFor = useRef<string | null>(null);

  const refetchOnce = useCallback(
    (url: string) => {
      if (refetchedFor.current === url) return false;
      refetchedFor.current = url;
      onRefetch();
      return true;
    },
    [onRefetch],
  );

  useEffect(() => {
    if (!imageUrl || !expiresAt) return undefined;
    const expiresMs = Date.parse(expiresAt);
    if (Number.isNaN(expiresMs)) return undefined;
    const delay = expiresMs - Date.now();
    if (delay <= 0) {
      refetchOnce(imageUrl);
      return undefined;
    }
    const timer = setTimeout(
      () => refetchOnce(imageUrl),
      Math.min(delay, MAX_TIMEOUT_MS),
    );
    return () => clearTimeout(timer);
  }, [imageUrl, expiresAt, refetchOnce]);

  const handleImageError = () => {
    if (!imageUrl) return;
    setBrokenUrl(imageUrl);
    refetchOnce(imageUrl);
  };

  return (
    <div>
      <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
        Goal
      </h5>
      {!goal ? (
        <p
          className="text-sm text-gray-600 dark:text-gray-400"
          data-testid="family-goal-empty"
        >
          No goal set.
        </p>
      ) : (
        <div className="flex flex-col md:flex-row gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1">
            <div>
              <label className={labelClass}>Title</label>
              <p className={`${valueClass} break-words`}>{goal.title}</p>
            </div>
            <div>
              <label className={labelClass}>Target</label>
              <p className={valueClass}>{formatEp(goal.targetEp)}</p>
            </div>
            <div>
              <label className={labelClass}>Reached At</label>
              <p className={valueClass}>
                {goal.reachedAt
                  ? formatDateTime(goal.reachedAt)
                  : "Not reached yet"}
              </p>
            </div>
          </div>
          <div className="md:w-56 flex-shrink-0">
            {imageUrl && brokenUrl !== imageUrl ? (
              <img
                src={imageUrl}
                alt="Family goal"
                onError={handleImageError}
                className="w-full h-32 object-cover rounded-md border border-gray-200 dark:border-gray-700"
                data-testid="family-goal-image"
              />
            ) : (
              <div
                className="w-full h-32 rounded-md border border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center text-xs text-gray-500 dark:text-gray-400"
                data-testid="family-goal-image-placeholder"
              >
                <PhotoIcon className="h-6 w-6 mb-1" />
                {imageUrl ? "Image could not be loaded" : "No image"}
              </div>
            )}
            {imageUrl && (
              <button
                type="button"
                onClick={onRemoveImage}
                disabled={actionsDisabled}
                className={`${redActionClass} mt-2 w-full`}
              >
                Remove image
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FamilyGoalHistory: React.FC<{ entries: FamilyGoalHistoryEntry[] }> = ({
  entries,
}) => (
  <div>
    <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
      Goal history
    </h5>
    {entries.length === 0 ? (
      <p className="text-sm text-gray-600 dark:text-gray-400">
        No earlier goals.
      </p>
    ) : (
      <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className={thClass}>Title</th>
              <th className={thClass}>Target</th>
              <th className={thClass}>Outcome</th>
              <th className={thClass}>Created</th>
              <th className={thClass}>Closed</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {entries.map((entry) => (
              <tr key={`${entry.goalId}-${entry.closedAt}`}>
                <td className={`${tdClass} whitespace-normal break-words`}>
                  {entry.title}
                </td>
                <td className={tdClass}>{formatEp(entry.targetEp)}</td>
                <td className={tdClass}>{humanize(entry.outcome)}</td>
                <td className={`${tdClass} text-xs`}>
                  {formatDateTime(entry.createdAt)}
                </td>
                <td className={`${tdClass} text-xs`}>
                  {formatDateTime(entry.closedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const FamilyEvents: React.FC<{ events: FamilyEvent[] }> = ({ events }) => (
  <div>
    <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
      Recent events
    </h5>
    {events.length === 0 ? (
      <p className="text-sm text-gray-600 dark:text-gray-400">No events.</p>
    ) : (
      <ul
        className="max-h-72 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 rounded-md border border-gray-200 dark:border-gray-700"
        data-testid="family-events"
      >
        {events.map((event) => (
          <li
            key={event.eventId}
            className="px-3 py-2 text-sm flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1"
          >
            <div className="min-w-0">
              <div className="font-medium text-gray-900 dark:text-white">
                {humanize(event.type)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                By {humanize(event.actorType)}
                {event.actorId ? ` (${event.actorId})` : ""}
                {event.targetMemberId ? ` · member ${event.targetMemberId}` : ""}
              </div>
              {event.reason && (
                <div className="text-xs text-gray-600 dark:text-gray-300 break-words">
                  Reason: {event.reason}
                </div>
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {formatDateTime(event.createdAt)}
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default FamilyAccountSection;
